import { useEffect } from "react";
import { SITE_NAME, SITE_URL } from "../config";

interface DocumentMeta {
  /** Page title WITHOUT the brand suffix (added automatically). */
  title: string;
  /** Meta description for this page. */
  description: string;
  /** Path (e.g. "/packages") used to build the canonical URL. */
  path: string;
}

/** Set or create a <meta name="..."> / <meta property="..."> tag. */
function setMetaTag(attr: "name" | "property", key: string, content: string) {
  let el = document.head.querySelector<HTMLMetaElement>(
    `meta[${attr}="${key}"]`
  );
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

/** Set or create the canonical <link>. */
function setCanonical(href: string) {
  let el = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

/**
 * Per-route document metadata for SEO. Sets the document title, description,
 * canonical URL, and the URL-dependent Open Graph tags on navigation.
 *
 * Note: social scrapers (WhatsApp/Facebook) do not execute JS, so they read
 * the static defaults in index.html. This hook improves titles/descriptions
 * for JS-rendering crawlers (e.g. Google) and in-app link behaviour.
 */
export function useDocumentMeta({ title, description, path }: DocumentMeta) {
  useEffect(() => {
    const fullTitle = `${title} | ${SITE_NAME}`;
    const canonical = `${SITE_URL}${path === "/" ? "" : path}`;

    document.title = fullTitle;
    setMetaTag("name", "description", description);
    setCanonical(canonical);

    setMetaTag("property", "og:title", fullTitle);
    setMetaTag("property", "og:description", description);
    setMetaTag("property", "og:url", canonical);
    setMetaTag("name", "twitter:title", fullTitle);
    setMetaTag("name", "twitter:description", description);
  }, [title, description, path]);
}
